/**
 * Контракты аутентификации.
 *
 * Пока OpenAPI-клиент не сгенерирован (`npm run api` требует запущенного
 * бэкенда), типы описаны здесь вручную и совпадают со схемами модуля auth.
 */

import { request, setAccessToken } from "@/shared/api/client";

export interface LoginRequest {
  email: string;
  password: string;
  totp_code?: string;
  account_code?: string;
}

export interface LoginResponse {
  access_token: string;
  expires_in: number;
  requires_second_factor: boolean;
  must_change_password: boolean;
}

export interface CurrentUser {
  id: number;
  email: string;
  full_name: string;
  short_name: string | null;
  system_role: "owner" | "admin" | "tech_admin" | "user" | "robot";
  is_active: boolean;
  totp_enabled: boolean;
  timezone: string | null;
  locale: string | null;
  ui_prefs: Record<string, unknown>;
  last_login_at: string | null;
  /** Откуда система знает этого человека: «local», «ldap», «oidc». */
  auth_provider: string;
}

export interface TotpSetup {
  secret: string;
  provisioning_uri: string;
  qr_code_svg: string;
  recovery_codes: string[];
}

export async function login(payload: LoginRequest): Promise<LoginResponse> {
  const response = await request<LoginResponse>("/api/auth/login", {
    method: "POST",
    body: payload,
  });
  if (response.access_token) {
    setAccessToken(response.access_token);
  }
  return response;
}

export async function logout(): Promise<void> {
  await request<undefined>("/api/auth/logout", { method: "POST" });
  setAccessToken(null);
}

export function me(): Promise<CurrentUser> {
  return request<CurrentUser>("/api/users/me");
}

export function setupTotp(): Promise<TotpSetup> {
  return request<TotpSetup>("/api/auth/2fa/setup", { method: "POST" });
}

export function confirmTotp(code: string): Promise<void> {
  return request<undefined>("/api/auth/2fa/confirm", { method: "POST", body: { code } });
}

export function disableTotp(password: string): Promise<void> {
  return request<undefined>("/api/auth/2fa/disable", { method: "POST", body: { password } });
}

export function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  return request<undefined>("/api/auth/password", {
    method: "POST",
    body: { current_password: currentPassword, new_password: newPassword },
  });
}

export function updatePreferences(prefs: {
  theme?: string;
  density?: string;
  skin?: string;
  colors?: Record<string, string>;
}): Promise<{ theme: string | null; density: string | null }> {
  return request("/api/users/me/preferences", { method: "PATCH", body: prefs });
}

/** Сколько живёт сессия: null — как в контуре, 0 — не истекает. */
export interface SessionLifetime {
  seconds: number | null;
  account_seconds: number;
}

export function getSessionLifetime(): Promise<SessionLifetime> {
  return request<SessionLifetime>("/api/users/me/session");
}

export function setSessionLifetime(seconds: number | null): Promise<SessionLifetime> {
  return request<SessionLifetime>("/api/users/me/session", {
    method: "PATCH",
    body: { seconds },
  });
}

/** Есть ли на форме входа кнопка провайдера и что на ней написано. */
export interface SsoOffer {
  is_enabled: boolean;
  button_label: string;
}

export function ssoOffer(): Promise<SsoOffer> {
  return request<SsoOffer>("/api/oidc/public");
}

/** Куда отправить браузер, чтобы человек назвался у провайдера. */
export function ssoStart(redirectUri: string): Promise<{ url: string }> {
  return request<{ url: string }>(
    `/api/oidc/start?redirect_uri=${encodeURIComponent(redirectUri)}`,
  );
}

/** Обменять код возврата на нашу сессию. */
export function ssoFinish(
  code: string,
  state: string,
): Promise<{ access_token: string; expires_in: number }> {
  const params = new URLSearchParams({ code, state });
  return request<{ access_token: string; expires_in: number }>(
    `/api/oidc/callback?${params.toString()}`,
    {
      method: "POST",
    },
  );
}
